/**
 * Applies a declarative dashboard plan from the API (no eval of arbitrary JS).
 * Supports: style banner, KPI + insight strips, tables (filter/aggregate), charts (single or list),
 * chart tableIndex aligned to spec.tables indices.
 * **Numbers always reference source Raw_* tables** — no pasted snapshot values for data blocks or KPIs.
 * Live LET/FILTER/SUMIFS over ListObjects’ **[#Data]** (Microsoft 365). If Excel rejects a formula, the
 * block stays empty and a warning is recorded (no silent fallback to static numbers).
 */

import { excelService } from './excelService.js';
import { getTableDataBodyRange, tableDataBodyHasRows } from './excelTableHelpers.js';

/**
 * Charts must not use the full spill reservation (often ~8k rows, mostly blank) — Excel for the web often
 * returns "internal error" on chart.add or later sync. Cap source rows for all chart types.
 */
const MAX_CHART_SOURCE_ROWS = 400;

function colLetter(n) {
  return excelService.getExcelColumn(n);
}

/** 1-based column index from letters (A=1, Z=26, AA=27). */
function columnLetterToIndex(letters) {
  let n = 0;
  const s = String(letters || 'A').toUpperCase().replace(/[^A-Z]/g, '') || 'A';
  for (let i = 0; i < s.length; i++) {
    n = n * 26 + (s.charCodeAt(i) - 64);
  }
  return n;
}

function columnsInSpan(fromLetter, toLetter) {
  return columnLetterToIndex(toLetter) - columnLetterToIndex(fromLetter) + 1;
}

/** Ensures every data row has exactly headers.length cells (Excel rejects jagged arrays). */
function normalizeHeaderRow(headers) {
  return (headers || []).map((h) => (h == null ? '' : h));
}

function sanitizeSheetName(name) {
  let s = String(name || 'AI_Dashboard').replace(/[\\/*?:\[\]]/g, '_').trim();
  if (!s) s = 'AI_Dashboard';
  if (s.length > 31) s = s.slice(0, 31);
  return s;
}

function sanitizeTableName(base, index) {
  const raw = `Tbl_${String(base || 'Dash').replace(/[^a-zA-Z0-9_]/g, '_')}_${index}`.slice(0, 240);
  return /^[a-zA-Z_]/.test(raw) ? raw : `t_${raw}`.slice(0, 250);
}

function levenshtein(a, b) {
  const s = String(a);
  const t = String(b);
  if (s.length > 40 || t.length > 40) return 99;
  const m = s.length;
  const n = t.length;
  const dp = new Array(n + 1);
  for (let j = 0; j <= n; j++) dp[j] = j;
  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j];
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      dp[j] = Math.min(dp[j] + 1, dp[j - 1] + 1, prev + cost);
      prev = tmp;
    }
  }
  return dp[n];
}

/** Collapse to lowercase alphanumerics so "Month Name" / "month_name" / "monthName" all match. */
function normalizeColumnKey(s) {
  return String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Allow more slack on longer names; keeps single-letter typos forgiving without over-matching short names. */
function fuzzyDistanceBudget(len) {
  if (len <= 3) return 0;
  if (len <= 5) return 1;
  if (len <= 10) return 2;
  return 3;
}

/**
 * Map LLM column name to source header index.
 * Order: exact (normalized) → unique substring (normalized) → bounded Levenshtein (normalized).
 * Normalization strips spaces/underscores/punctuation so "Account Class" / "account_class" / "AccountClass" all tie.
 * @param {string} requested
 * @param {string[]} headers
 * @returns {number} -1 if no confident match
 */
function resolveColumnIndex(requested, headers) {
  const req = String(requested ?? '').trim();
  if (!req) return -1;
  const reqKey = normalizeColumnKey(req);
  if (!reqKey) return -1;
  const headerKeys = headers.map((h) => normalizeColumnKey(h));

  /* 1. Exact normalized match — wins even when a longer header contains the same substring. */
  const exact = headerKeys.indexOf(reqKey);
  if (exact >= 0) return exact;

  /* 2. Unique substring match (prefer header containing request; else request containing header). */
  const containsReq = [];
  for (let i = 0; i < headerKeys.length; i++) {
    if (!headerKeys[i]) continue;
    if (headerKeys[i].includes(reqKey) || reqKey.includes(headerKeys[i])) {
      containsReq.push(i);
    }
  }
  if (containsReq.length === 1) return containsReq[0];

  /* 3. Bounded Levenshtein on normalized keys. Score = (distance, |lenDelta|) ascending. */
  const budget = fuzzyDistanceBudget(reqKey.length);
  let bestIdx = -1;
  let bestScore = [Infinity, Infinity];
  for (let i = 0; i < headerKeys.length; i++) {
    const hk = headerKeys[i];
    if (!hk) continue;
    if (Math.abs(hk.length - reqKey.length) > budget) continue;
    const d = levenshtein(hk, reqKey);
    if (d > budget) continue;
    const score = [d, Math.abs(hk.length - reqKey.length)];
    if (score[0] < bestScore[0] || (score[0] === bestScore[0] && score[1] < bestScore[1])) {
      bestScore = score;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/** Small sample of header names for error messages so the user can see what's actually in the table. */
function exampleHeaderList(headers, limit = 6) {
  const cleaned = (headers || []).map((h) => String(h ?? '').trim()).filter(Boolean);
  if (!cleaned.length) return '(no headers)';
  if (cleaned.length <= limit) return cleaned.join(', ');
  return `${cleaned.slice(0, limit).join(', ')}, …`;
}

/** Excel structured ref: `t[Col]` or `t[[Col With Space]]` inside LET. */
function structuredCol(tVar, colName) {
  const c = String(colName || '').trim();
  if (!c) return null;
  if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(c)) return `${tVar}[${c}]`;
  return `${tVar}[[${c.replace(/\]/g, '')}]]`;
}

/**
 * Build a control mask for one interactive filter dropdown. The mask evaluates to 1 when
 * the dropdown holds its "(All)" sentinel (pass-through) OR when the data column equals the
 * dropdown value. Wrapped to ensure it's always a 0/1 array, never a text comparison.
 *
 * @param {string} columnRef - already-built structured ref like `tbl[PartyName]`.
 * @param {string} cellAddr  - absolute cell reference like `$A$4`.
 * @param {string} sentinel  - the "All" placeholder stored in the cell (default `"(All)"`).
 */
function controlMaskExpr(columnRef, cellAddr, sentinel) {
  const s = String(sentinel ?? '(All)').replace(/"/g, '""');
  return `((${cellAddr}="${s}")+(${columnRef}=${cellAddr}))`;
}

/**
 * When the dropdown lists display text from a lookup table (e.g. PartyName on Raw_Parties) but the
 * fact table filters on a key column (e.g. PartyId on Raw_TrialBalance), match with XLOOKUP.
 * Requires Microsoft 365 (same as the rest of this dashboard stack).
 */
function controlMaskLookupExpr(factColumnRef, cellAddr, sentinel, listTableName, listDisplayColumn, listJoinKeyColumn) {
  const lt = String(listTableName || '').trim();
  const dispRef = structuredCol(lt, String(listDisplayColumn || '').trim());
  const keyRef = structuredCol(lt, String(listJoinKeyColumn || '').trim());
  const s = String(sentinel ?? '(All)').replace(/"/g, '""');
  if (!lt || !dispRef || !keyRef) return controlMaskExpr(factColumnRef, cellAddr, sentinel);
  return `((${cellAddr}="${s}")+(${factColumnRef}=XLOOKUP(${cellAddr},${dispRef},${keyRef},"")))`;
}

/**
 * Boolean array for FILTER's second argument.
 *
 * Historically this function emitted LET-aliased refs (`t[Col]`). That does NOT work in Excel Online —
 * structured-column syntax is only valid on real table names, not on LET variables bound to a dynamic
 * array. The engine silently rejects those formulas and Office.js surfaces a generic "invalid formula"
 * error. We now emit **direct** structured refs (`TableName[Col]`) which work identically on Desktop,
 * Mac, and Online.
 *
 * @param {string} tableName Real workbook table name (e.g. `tbl_RawTrialBalance`).
 * @param {Array<{column: string, kind: string, value: string}>} filters Hard-coded WHERE clauses from the plan.
 * @param {Array<{
 *   columnName: string, cellAddr: string, sentinel: string,
 *   maskKind?: 'direct'|'xlookup', listTable?: string, listDisplayColumn?: string, listJoinKeyColumn?: string
 * }>} [controls] Interactive filter dropdowns.
 * @returns {string|null}
 */
function buildFilterIncludeArray(tableName, headers, filters, controls = []) {
  const parts = [];
  for (const f of filters) {
    const j = resolveColumnIndex(f.column, headers);
    if (j < 0) return null;
    const colRef = structuredCol(tableName, String(headers[j]).trim());
    if (!colRef) return null;
    const kind = String(f.kind || 'equals').toLowerCase();
    const fv = String(f.value ?? '').replace(/"/g, '""');
    if (kind === 'contains') {
      parts.push(`(ISNUMBER(SEARCH("${fv}",${colRef})))`);
    } else if (kind === 'startswith') {
      parts.push(`(LEFT(${colRef},LEN("${fv}"))="${fv}")`);
    } else if (Number.isFinite(Number(f.value)) && String(f.value).trim() === String(Number(f.value))) {
      parts.push(`(N(${colRef})=${Number(f.value)})`);
    } else {
      const n = Number(String(f.value ?? '').trim().replace(',', '.'));
      const looksNumeric = Number.isFinite(n) && /^-?\d+(\.\d+)?$/.test(String(f.value ?? '').trim().replace(/\s/g, ''));
      if (looksNumeric) {
        parts.push(`((N(${colRef})=${n})+(${colRef}="${fv}"))>0`);
      } else {
        parts.push(`(${colRef}="${fv}")`);
      }
    }
  }
  /* Each interactive control contributes an "(All)-or-equals" mask. Resolved column names are
     already normalized against the same header list used by the plan's hard filters. */
  for (const c of controls || []) {
    const j = resolveColumnIndex(c.columnName, headers);
    if (j < 0) return null;
    const colRef = structuredCol(tableName, String(headers[j]).trim());
    if (!colRef) return null;
    if (c.maskKind === 'xlookup' && c.listTable && c.listDisplayColumn && c.listJoinKeyColumn) {
      parts.push(
        controlMaskLookupExpr(colRef, c.cellAddr, c.sentinel, c.listTable, c.listDisplayColumn, c.listJoinKeyColumn)
      );
    } else {
      parts.push(controlMaskExpr(colRef, c.cellAddr, c.sentinel));
    }
  }
  if (!parts.length) {
    /* No criteria at all: row-aligned TRUE mask (scalar TRUE can fail Office.js with FILTER). */
    return `(SEQUENCE(ROWS(${tableName}[#Data]),1,1,1)=1)`;
  }
  return `(${parts.join('*')})`;
}

/**
 * Projection (SELECT cols FROM table WHERE filters [AND control-dropdowns]).
 *
 * Emits direct structured references to the source table (never LET-aliased `t[Col]`, which Excel
 * Online rejects). Optional `controls` are interactive dropdown cells that extend the FILTER mask
 * with `((cell="(All)") + (tbl[Col]=cell))` per control, so picking a value recomputes everything.
 *
 * @returns {string|null} formula beginning with =
 */
// eslint-disable-next-line no-unused-vars
function buildLiveProjectionFormula(_sheetName, tableName, headers, cols, filters, controls = []) {
  const colIdx = cols.map((c) => resolveColumnIndex(c, headers));
  if (colIdx.some((i) => i < 0)) return null;
  const choose = colIdx.map((i) => i + 1).join(',');
  const tn = String(tableName || '').trim();
  if (!tn) return null;
  const inc = buildFilterIncludeArray(tn, headers, filters, controls);
  if (inc === null) return null;
  const inner = `FILTER(${tn}[#Data],${inc})`;
  return `=IFERROR(IFERROR(CHOOSECOLS(${inner},${choose}),""),"")`;
}

/**
 * Aggregate (SELECT keys, SUM(val)|SUMABS(val) FROM table WHERE filters [AND controls] GROUP BY keys).
 *
 * Uses **SUMPRODUCT over the pre-filtered array `bf`** instead of SUMIFS over the full table. Why:
 * SUMIFS bypasses bf and would ignore our interactive filter dropdowns entirely (the dropdown effect
 * would be visible in which groups appear in `u`, but each group's sum would still span the whole
 * unfiltered table). Summing over bf makes filter controls AND hard filters both flow through to
 * the per-group numbers.
 *
 * @param {{ keyColumns: string[], sumColumns: string[], aggregation?: 'sum'|'abssum' }} agg
 * @returns {string|null}
 */
// eslint-disable-next-line no-unused-vars
function buildLiveAggregateFormula(_sheetName, tableName, headers, agg, filters, controls = []) {
  const sums = agg.sumColumns || [];
  const keys = agg.keyColumns || [];
  if (sums.length !== 1 || !keys.length) return null;
  const ki = keys.map((k) => resolveColumnIndex(k, headers));
  const si = resolveColumnIndex(sums[0], headers);
  if (ki.some((i) => i < 0) || si < 0) return null;
  const tn = String(tableName || '').trim();
  if (!tn) return null;
  const keyOneBased = ki.map((i) => i + 1).join(',');
  const sumPos1 = si + 1;
  const inc = buildFilterIncludeArray(tn, headers, filters, controls);
  if (inc === null) return null;

  const aggKind = String(agg.aggregation || 'sum').toLowerCase();
  /* Key equality masks over bf (CHOOSECOLS extracts the key's column index within the ORIGINAL
     table; bf has the same column layout as tbl[#Data]). */
  const keyMasks = ki.map((i, idx) => `(CHOOSECOLS(bf,${i + 1})=INDEX(r,,${idx + 1}))`);
  const valExpr = aggKind === 'abssum'
    ? `ABS(CHOOSECOLS(bf,${sumPos1}))`
    : `CHOOSECOLS(bf,${sumPos1})`;
  const perRowExpr = `SUMPRODUCT(${[...keyMasks, valExpr].join('*')})`;
  const byrow = `BYROW(u,LAMBDA(r,${perRowExpr}))`;

  return `=IFERROR(LET(bf,FILTER(${tn}[#Data],${inc}),u,IFERROR(UNIQUE(CHOOSECOLS(bf,${keyOneBased})),""),IF(ROWS(u)*COLUMNS(u)=0,"",HSTACK(u,${byrow}))),"")`;
}

/**
 * `Tbl[[#Data],[Column]]` for SUMIFS / SUM / COUNTA (no LET — best Office.js compatibility).
 * Unqualified: table names are workbook-unique, so this is the most dynamic / rename-safe form.
 */
// eslint-disable-next-line no-unused-vars
function qualifiedDataColumnRef(_sheetName, tableName, columnName) {
  const tn = String(tableName || '').trim();
  const cn = String(columnName || '').trim().replace(/\]/g, '');
  if (!tn || !cn) return null;
  return `${tn}[[#Data],[${cn}]]`;
}

/**
 * Plain SUM of one column (whole data body). Only use when there are **no** filters — otherwise
 * debits/credits net to ~0 and you get scientific‑notation dust.
 */
function bareSumDataColumnFormula(sheetName, tableName, columnName) {
  const r = qualifiedDataColumnRef(sheetName, tableName, columnName);
  if (!r) return null;
  return `=IFERROR(SUM(${r}),"")`;
}

/**
 * KPI / fallback totals: **SUMIFS** on [#Data] columns so filters match the model (revenue rows, year, etc.).
 * No LET wrapper — avoids host rejections and wrong nets from unfiltered SUM.
 *
 * When `controls` is non-empty, switches to a SUMPRODUCT-with-masks pattern so interactive filter
 * dropdowns at the top of the dashboard propagate through to this total (SUMIFS can't reference a
 * cell as an "(All)" sentinel via array logic).
 */
function buildTotalSumFormula(sheetName, tableName, headers, sumColName, filters, controls = []) {
  const si = resolveColumnIndex(sumColName, headers);
  if (si < 0) return null;
  const sumC = String(headers[si]).trim();
  const sumRange = qualifiedDataColumnRef(sheetName, tableName, sumC);
  if (!sumRange) return null;

  if (controls && controls.length) {
    const maskExpr = buildKpiRowMaskExpr(tableName, headers, filters, controls);
    if (maskExpr === null) return null;
    if (!maskExpr) return `=IFERROR(SUM(${sumRange}),"")`;
    return `=IFERROR(SUMPRODUCT(${sumRange}*${maskExpr}),"")`;
  }

  if (!filters.length) {
    return `=IFERROR(SUM(${sumRange}),"")`;
  }
  let s = `SUMIFS(${sumRange}`;
  for (const f of filters) {
    const j = resolveColumnIndex(f.column, headers);
    if (j < 0) return null;
    const critRange = qualifiedDataColumnRef(sheetName, tableName, String(headers[j]).trim());
    if (!critRange) return null;
    const kind = String(f.kind || 'equals').toLowerCase();
    const fv = String(f.value ?? '').replace(/"/g, '""');
    let crit;
    if (kind === 'contains') crit = `"*${fv}*"`;
    else if (kind === 'startswith') crit = `"${fv}*"`;
    else if (Number.isFinite(Number(f.value)) && String(f.value).trim() === String(Number(f.value)))
      crit = String(Number(f.value));
    else crit = `"${fv}"`;
    s += `,${critRange},${crit}`;
  }
  s += ')';
  return `=IFERROR(${s},"")`;
}

/**
 * Build a combined row-mask expression (as a single `(mask1)*(mask2)*…` string) over a [#Data] column
 * space, used by KPI formulas that need to honour both hard filters AND interactive controls.
 *
 * @returns {string|null}  `null` on unresolvable column, `""` when there's literally nothing to mask
 *                         (caller should fall back to SUM/COUNT across the full column).
 */
function buildKpiRowMaskExpr(tableName, headers, filters, controls) {
  const parts = [];
  for (const flt of (filters || [])) {
    const j = resolveColumnIndex(flt.column, headers);
    if (j < 0) return null;
    const ref = qualifiedDataColumnRef(null, tableName, String(headers[j]).trim());
    if (!ref) return null;
    const kind = String(flt.kind || 'equals').toLowerCase();
    const fv = String(flt.value ?? '').replace(/"/g, '""');
    if (kind === 'contains') parts.push(`ISNUMBER(SEARCH("${fv}",${ref}))`);
    else if (kind === 'startswith') parts.push(`(LEFT(${ref},LEN("${fv}"))="${fv}")`);
    else if (Number.isFinite(Number(flt.value)) && String(flt.value).trim() === String(Number(flt.value))) {
      parts.push(`(N(${ref})=${Number(flt.value)})`);
    } else {
      parts.push(`(${ref}="${fv}")`);
    }
  }
  for (const c of (controls || [])) {
    const j = resolveColumnIndex(c.columnName, headers);
    if (j < 0) return null;
    const factCol = String(headers[j]).trim();
    const ref = qualifiedDataColumnRef(null, tableName, factCol);
    if (!ref) return null;
    if (c.maskKind === 'xlookup' && c.listTable && c.listDisplayColumn && c.listJoinKeyColumn) {
      parts.push(controlMaskLookupExpr(ref, c.cellAddr, c.sentinel, c.listTable, c.listDisplayColumn, c.listJoinKeyColumn));
    } else {
      parts.push(controlMaskExpr(ref, c.cellAddr, c.sentinel));
    }
  }
  return parts.length ? `(${parts.join('*')})` : '';
}

/** First usable amount column for KPI totals linked to the source table. */
function pickDefaultSumColumnName(headers) {
  const h = (headers || []).map((x) => String(x ?? '').trim());
  const idx = h.findIndex((name) =>
    /balancechange|amount|netchange|balance$/i.test(name.replace(/\s+/g, ''))
  );
  return idx >= 0 ? h[idx] : null;
}

/** Excel criteria fragment from a single filter (shared by SUMIFS/COUNTIFS/AVERAGEIFS/MIN/MAXIFS). */
function filterCriteria(kind, fv) {
  const safe = String(fv ?? '').replace(/"/g, '""');
  const k = String(kind || 'equals').toLowerCase();
  if (k === 'contains') return `"*${safe}*"`;
  if (k === 'startswith') return `"${safe}*"`;
  const numeric = Number(safe.trim().replace(',', '.'));
  if (Number.isFinite(numeric) && /^-?\d+([.,]\d+)?$/.test(safe.trim())) {
    return String(numeric);
  }
  return `"${safe}"`;
}

/**
 * Per-KPI formula: aggregate kinds supported — sum, count, countdistinct, average, min, max, abssum.
 * Counts work on the key column (or the first column if none provided). Average/min/max require a numeric column.
 * Returns { formula, reason } — reason is set when the formula can't be built so we can surface it to chat.
 *
 * When `controls` is non-empty, the formula switches to a FILTER-based pattern so the KPI recomputes
 * live when the user changes an interactive dropdown at the top of the dashboard. Without controls,
 * we keep the existing SUMIFS/AVERAGEIFS/MINIFS/MAXIFS/COUNTIFS paths — faster for large tables and
 * more forgiving on older Excel hosts.
 *
 * @param {{
 *   tableName: string,
 *   headers: string[],
 *   aggregation?: 'sum'|'count'|'countdistinct'|'average'|'min'|'max'|'abssum',
 *   sumColumn?: string,
 *   filters?: { column: string, kind: string, value: string }[],
 *   controls?: { columnName: string, cellAddr: string, sentinel: string }[]
 * }} args
 */
function buildAggregateKpiFormula(args) {
  const { tableName, headers, aggregation, sumColumn, filters, controls } = args;
  const agg = String(aggregation || 'sum').toLowerCase();
  const f = Array.isArray(filters) ? filters : [];
  const ctrls = Array.isArray(controls) ? controls : [];
  const hasControls = ctrls.length > 0;

  /** Pick the value column: explicit > default amount detector > first column for count-only aggregations. */
  let colIdx = sumColumn ? resolveColumnIndex(sumColumn, headers) : -1;
  if (colIdx < 0 && agg !== 'count' && agg !== 'countdistinct') {
    const guess = pickDefaultSumColumnName(headers);
    if (guess) colIdx = resolveColumnIndex(guess, headers);
  }
  if (colIdx < 0 && (agg === 'count' || agg === 'countdistinct')) {
    colIdx = 0;
  }
  if (colIdx < 0) {
    return { formula: null, reason: `no value column (requested "${sumColumn || 'auto'}"; headers: ${exampleHeaderList(headers)})` };
  }
  const valCol = String(headers[colIdx] ?? '').trim();
  const valRange = qualifiedDataColumnRef(null, tableName, valCol);
  if (!valRange) return { formula: null, reason: 'invalid value column reference' };

  /** Interactive-controls path: single array-formula pattern that covers all aggregation kinds
      AND keeps the dropdown cells live. Values below are computed over `FILTER(valCol, mask)` so
      when the user picks "(All)" the filter mask is all-TRUE and we sum the whole column. */
  if (hasControls) {
    const maskExpr = buildKpiRowMaskExpr(tableName, headers, f, ctrls);
    if (maskExpr === null) {
      return { formula: null, reason: `could not resolve a filter/control column (headers: ${exampleHeaderList(headers)})` };
    }
    /* maskExpr === "" means no filters + no controls, which shouldn't happen here (hasControls=true) */
    const effMask = maskExpr || `(SEQUENCE(ROWS(${tableName}[#Data]),1,1,1)=1)`;
    if (agg === 'count') {
      return { formula: `=IFERROR(SUMPRODUCT((${valRange}<>"")*${effMask}),0)`, reason: null };
    }
    if (agg === 'countdistinct') {
      return { formula: `=IFERROR(ROWS(UNIQUE(FILTER(${valRange},${effMask}*(${valRange}<>"")))),0)`, reason: null };
    }
    if (agg === 'average') {
      return { formula: `=IFERROR(AVERAGE(FILTER(${valRange},${effMask})),"")`, reason: null };
    }
    if (agg === 'min') {
      return { formula: `=IFERROR(MIN(FILTER(${valRange},${effMask})),"")`, reason: null };
    }
    if (agg === 'max') {
      return { formula: `=IFERROR(MAX(FILTER(${valRange},${effMask})),"")`, reason: null };
    }
    if (agg === 'abssum' || agg === 'sumabs') {
      return { formula: `=IFERROR(SUMPRODUCT(ABS(${valRange})*${effMask}),"")`, reason: null };
    }
    /* default: sum */
    return { formula: `=IFERROR(SUMPRODUCT(${valRange}*${effMask}),"")`, reason: null };
  }

  /** Build criteria pairs; bail with a useful reason if any filter column can't be resolved. */
  const critPairs = [];
  for (const flt of f) {
    const j = resolveColumnIndex(flt.column, headers);
    if (j < 0) {
      return { formula: null, reason: `filter column "${flt.column}" not found (headers: ${exampleHeaderList(headers)})` };
    }
    const ref = qualifiedDataColumnRef(null, tableName, String(headers[j]).trim());
    if (!ref) return { formula: null, reason: 'invalid filter column reference' };
    critPairs.push([ref, filterCriteria(flt.kind, flt.value)]);
  }
  const critCsv = critPairs.map((p) => `${p[0]},${p[1]}`).join(',');

  if (agg === 'count') {
    /* COUNTA counts non-empty cells; with filters we need COUNTIFS. */
    if (!critPairs.length) return { formula: `=IFERROR(COUNTA(${valRange}),0)`, reason: null };
    return { formula: `=IFERROR(COUNTIFS(${critCsv}),0)`, reason: null };
  }
  if (agg === 'countdistinct') {
    /* Distinct count of value column matching filters (Microsoft 365: UNIQUE+FILTER). */
    if (!critPairs.length) {
      return {
        formula: `=IFERROR(ROWS(UNIQUE(FILTER(${valRange},${valRange}<>""))),0)`,
        reason: null,
      };
    }
    const mask = critPairs
      .map(([ref, crit]) => {
        /* COUNTIFS-style criteria converted to array boolean for FILTER. */
        const c = String(crit);
        if (c.startsWith('"*') && c.endsWith('*"')) {
          const needle = c.slice(2, -2).replace(/""/g, '"');
          return `ISNUMBER(SEARCH("${needle}",${ref}))`;
        }
        if (c.endsWith('*"') && c.startsWith('"')) {
          const needle = c.slice(1, -2).replace(/""/g, '"');
          return `(LEFT(${ref},LEN("${needle}"))="${needle}")`;
        }
        if (/^-?\d+([.,]\d+)?$/.test(c)) return `(N(${ref})=${c})`;
        return `(${ref}=${c})`;
      })
      .join('*');
    return {
      formula: `=IFERROR(ROWS(UNIQUE(FILTER(${valRange},(${mask})*(${valRange}<>"")))),0)`,
      reason: null,
    };
  }
  if (agg === 'average') {
    if (!critPairs.length) return { formula: `=IFERROR(AVERAGE(${valRange}),"")`, reason: null };
    return { formula: `=IFERROR(AVERAGEIFS(${valRange},${critCsv}),"")`, reason: null };
  }
  if (agg === 'min') {
    if (!critPairs.length) return { formula: `=IFERROR(MIN(${valRange}),"")`, reason: null };
    return { formula: `=IFERROR(MINIFS(${valRange},${critCsv}),"")`, reason: null };
  }
  if (agg === 'max') {
    if (!critPairs.length) return { formula: `=IFERROR(MAX(${valRange}),"")`, reason: null };
    return { formula: `=IFERROR(MAXIFS(${valRange},${critCsv}),"")`, reason: null };
  }
  if (agg === 'abssum' || agg === 'sumabs') {
    /* Activity total — avoids the trial-balance "debits vs credits net to zero" trap.
       With filters, mask the value range with the AND of per-criteria boolean arrays. */
    if (!critPairs.length) {
      return { formula: `=IFERROR(SUMPRODUCT(ABS(${valRange})),"")`, reason: null };
    }
    const mask = critPairs
      .map(([ref, crit]) => {
        const c = String(crit);
        if (c.startsWith('"*') && c.endsWith('*"')) {
          const needle = c.slice(2, -2).replace(/""/g, '"');
          return `ISNUMBER(SEARCH("${needle}",${ref}))`;
        }
        if (c.endsWith('*"') && c.startsWith('"')) {
          const needle = c.slice(1, -2).replace(/""/g, '"');
          return `(LEFT(${ref},LEN("${needle}"))="${needle}")`;
        }
        if (/^-?\d+([.,]\d+)?$/.test(c)) return `(N(${ref})=${c})`;
        return `(${ref}=${c})`;
      })
      .join('*');
    return {
      formula: `=IFERROR(SUMPRODUCT(ABS(${valRange})*(${mask})),"")`,
      reason: null,
    };
  }
  /* Default: sum. */
  if (!critPairs.length) return { formula: `=IFERROR(SUM(${valRange}),"")`, reason: null };
  return { formula: `=IFERROR(SUMIFS(${valRange},${critCsv}),"")`, reason: null };
}

function mapChartType(t) {
  const x = String(t || 'ColumnClustered');
  if (x === 'Line') return Excel.ChartType.line;
  if (x === 'LineMarkers') return Excel.ChartType.lineMarkers;
  if (x === 'Pie') return Excel.ChartType.pie;
  if (x === 'Doughnut') return Excel.ChartType.doughnut;
  if (x === 'BarClustered') return Excel.ChartType.barClustered;
  return Excel.ChartType.columnClustered;
}

function applyStyleBanner(sheet, style, startRow) {
  let row = startRow;
  const st = style || {};
  if (st.titleBarText) {
    const endC = String(st.titleMergeToColumn || 'H').toUpperCase().replace(/[^A-Z]/g, '') || 'H';
    const bar = sheet.getRange(`A${row}:${endC}${row}`);
    const span = columnsInSpan('A', endC);
    const titleRow = Array(span).fill('');
    titleRow[0] = String(st.titleBarText);
    bar.values = [titleRow];
    bar.merge(true);
    bar.format.horizontalAlignment = 'Center';
    bar.format.verticalAlignment = 'Center';
    bar.format.font.bold = true;
    bar.format.font.size = 14;
    if (st.titleBarFontHex) bar.format.font.color = st.titleBarFontHex;
    if (st.titleBarFillHex) bar.format.fill.color = st.titleBarFillHex;
    row += 1;
  }
  if (st.hideGridlines) {
    try {
      sheet.showGridlines = false;
    } catch { /* older hosts */ }
  }
  return row;
}

/**
 * Data bar conditional format on a single-column range (numeric values only).
 * Scoped to a modest row cap (not the whole spill reservation) — overly long ranges slow Excel Online's
 * rendering and leak bars into the ~8k empty reservation cells. Failures are ignored so older hosts
 * without ConditionalFormatType.dataBar still render the table cleanly.
 */
function applyDataBarsToColumn(sheet, colLetterOf, fromRow, toRow, style) {
  try {
    const cap = Math.min(toRow, fromRow + 199);
    if (cap < fromRow) return;
    const addr = `${colLetterOf}${fromRow}:${colLetterOf}${cap}`;
    const rg = sheet.getRange(addr);
    const cf = rg.conditionalFormats.add(Excel.ConditionalFormatType.dataBar);
    const db = cf.dataBar;
    /* Use the dashboard accent for the bar when provided, else a muted blue. */
    const barHex = style?.titleBarFillHex || '#5B9BD5';
    db.positiveFormat.fillColor = barHex;
    db.positiveFormat.gradientFill = true;
    db.showDataBarOnly = false;
    try { db.negativeFormat.fillColor = '#E06666'; } catch { /* some hosts no-op */ }
    try { db.axisColor = '#7F7F7F'; } catch { /* some hosts no-op */ }
  } catch { /* older host / no conditionalFormats support — skip silently */ }
}

/** Apply thin outline border to a range on all four sides + interior. Individual `try` per side — some hosts choke on certain border identifiers. */
function applyCardBorder(range, hex) {
  const color = hex || '#C8C8C8';
  const ids = ['EdgeTop', 'EdgeBottom', 'EdgeLeft', 'EdgeRight'];
  for (const id of ids) {
    try {
      const b = range.format.borders.getItem(id);
      b.style = 'Continuous';
      b.color = color;
      b.weight = 'Thin';
    } catch { /* host variance */ }
  }
}

/**
 * Render interactive filter dropdowns above the KPI strip. Each control becomes a small card
 * (label band on top, editable dropdown cell below). Distinct dropdown values come from reading
 * the source table column via Office.js — **not** from a spilled helper formula — because Excel
 * Online ignores dynamic `#` spill references inside data-validation list sources (dropdown would
 * only show "(All)"). Lists refresh each time the dashboard plan is applied.
 *
 * Registers each control in `registry` keyed by normalized table name; downstream formula builders
 * look it up and AND-mask every computation against the selected dropdown value. Picking "(All)"
 * passes through (no filter).
 *
 * @param {Excel.RequestContext} context
 * @param {Excel.Workbook} wb
 * @param {Excel.Worksheet} sheet
 * @param {number} startRow Row to place the first label band on.
 * @param {Array<{column:string, sourceTable:string, label?:string}>} controls
 * @param {object|undefined} style Dashboard style bag (reuses titleBarFillHex / titleBarFontHex for the label band).
 * @param {(sheet:string|undefined, table:string) => Promise<{srcSheet:string, srcTable:string, headers:string[]}|null>} resolveTable
 * @param {string[]} warnings Collected to surface skipped controls back to chat.
 * @returns {Promise<{ nextRow: number, registry: Map<string, Array<{columnName:string, cellAddr:string, sentinel:string}>> }>}
 */
/**
 * Populate a vertical list of dropdown values under "(All)" by reading the source table column.
 *
 * Excel Online and several builds **do not expand** dynamic-array spill references (`=AZ1#`) inside
 * data-validation list sources — the dropdown silently shows only the first cell ("(All)"). Reading
 * distinct values via the Table API and binding validation to an explicit range (`=$AZ$1:$AZ$12`)
 * works everywhere.
 *
 * @returns {Promise<number>} Number of rows written (including "(All)" row).
 */
async function populateFilterDropdownListFromTable(context, wb, dashboardSheet, bound, colResolved, helperCol, controlIndex, warnings) {
  const ws = wb.worksheets.getItem(bound.srcSheet);
  const tbl = ws.tables.getItem(bound.srcTable);
  /* Column index aligns with workbookTable header order — same index order as tbl.columns collection. */
  let colItem;
  try {
    colItem = tbl.columns.getItemAt(colResolved);
  } catch {
    tbl.columns.load('items');
    await context.sync();
    const it = tbl.columns.items[colResolved];
    if (!it) {
      warnings.push(`filterControls[${controlIndex}] — column index ${colResolved} out of range on ${bound.srcTable}; dropdown list not built.`);
      return 1;
    }
    colItem = tbl.columns.getItem(it.name);
  }

  const body = colItem.getDataBodyRange();
  body.load(['rowCount', 'values']);
  await context.sync();

  const set = new Set();
  const vals = body.values || [];
  for (let ri = 0; ri < vals.length; ri++) {
    const v = vals[ri][0];
    if (v == null || v === '') continue;
    const s = String(v).trim();
    if (s !== '') set.add(s);
  }

  const sorted = [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const MAX_DISTINCT = 2000;
  const capped = sorted.slice(0, MAX_DISTINCT);
  if (sorted.length > MAX_DISTINCT) {
    warnings.push(
      `filterControls[${controlIndex}] — distinct list capped at ${MAX_DISTINCT} values (${sorted.length} unique in "${actualColName}").`
    );
  }

  const SENTINEL = '(All)';
  const rows = [[SENTINEL], ...capped.map((x) => [x])];
  const top = `${helperCol}1`;
  const bot = `${helperCol}${rows.length}`;
  /* Clear a generous chunk so re-applied dashboards don't leave stale names below a shorter list. */
  try {
    dashboardSheet.getRange(`${helperCol}1:${helperCol}5000`).clear(Excel.ClearApplyTo.contents);
  } catch {
    /* ignore */
  }
  dashboardSheet.getRange(`${top}:${bot}`).values = rows;

  return rows.length;
}

async function writeFilterControls(context, wb, sheet, startRow, controls, style, resolveTable, warnings) {
  const registry = new Map();
  if (!Array.isArray(controls) || !controls.length) {
    return { nextRow: startRow, registry };
  }

  const list = controls.slice(0, 6);
  const labelFill = style?.titleBarFillHex || '#1F4E79';
  const labelFont = style?.titleBarFontHex || '#FFFFFF';
  const cellFill = '#FFFFFF';
  const cellFont = '#1F3864';
  const borderHex = '#BFCBDE';

  const tileWidth = 2;
  const tileGap = 1;
  const tileStep = tileWidth + tileGap;

  const labelRow = startRow;
  const valueRow = startRow + 1;

  /* Hidden helper columns live far right so they never visually collide with KPIs / charts / tables.
     Column 52 = "AZ"; each subsequent control picks the next column. We hide after writing. */
  const HELPER_COL_START = 52;
  const SENTINEL = '(All)';

  let col = 1;
  let placed = 0;
  for (let i = 0; i < list.length; i++) {
    const ctrl = list[i] || {};
    const column = String(ctrl.column || '').trim();
    const sourceTable = String(ctrl.sourceTable || '').trim();
    if (!column || !sourceTable) {
      warnings.push(`filterControls[${i}] skipped — column/sourceTable missing.`);
      continue;
    }

    const bound = await resolveTable(undefined, sourceTable);
    if (!bound) {
      warnings.push(`filterControls[${i}] skipped — table "${sourceTable}" not found in this workbook.`);
      continue;
    }
    const colResolved = resolveColumnIndex(column, bound.headers);
    if (colResolved < 0) {
      warnings.push(`filterControls[${i}] skipped — column "${column}" not in ${sourceTable} (available: ${exampleHeaderList(bound.headers)}).`);
      continue;
    }
    const actualColName = String(bound.headers[colResolved]).trim();
    const listSourceTable = String(ctrl.listSourceTable || ctrl.ListSourceTable || '').trim();
    const listColumn = String(ctrl.listColumn || ctrl.ListColumn || '').trim();
    const listJoinKeyColumn = String(ctrl.listJoinKeyColumn || ctrl.ListJoinKeyColumn || '').trim();

    /** Optional: build dropdown from another Raw_* table (e.g. tbl_RawParties.PartyName) while still filtering `SourceTable` via `Column` (e.g. PartyId). */
    let populateBound = bound;
    let populateColIdx = colResolved;
    let listDisplayResolved = null;
    let listKeyResolved = null;
    if (listSourceTable && listColumn) {
      const lb = await resolveTable(undefined, listSourceTable);
      if (!lb) {
        warnings.push(
          `filterControls[${i}] listSourceTable "${listSourceTable}" not found — dropdown values fall back to "${actualColName}" on ${sourceTable}.`
        );
      } else {
        const lci = resolveColumnIndex(listColumn, lb.headers);
        if (lci < 0) {
          warnings.push(
            `filterControls[${i}] listColumn "${listColumn}" not in ${listSourceTable} (${exampleHeaderList(lb.headers)}) — dropdown falls back to fact column "${actualColName}".`
          );
        } else {
          populateBound = lb;
          populateColIdx = lci;
          listDisplayResolved = String(lb.headers[lci]).trim();
        }
        if (listJoinKeyColumn) {
          const kix = resolveColumnIndex(listJoinKeyColumn, lb.headers);
          if (kix < 0) {
            warnings.push(
              `filterControls[${i}] listJoinKeyColumn "${listJoinKeyColumn}" not in ${listSourceTable} — Party/key XLOOKUP filter disabled (direct match only).`
            );
          } else {
            listKeyResolved = String(lb.headers[kix]).trim();
          }
        }
      }
    } else if (listJoinKeyColumn) {
      warnings.push(
        `filterControls[${i}] listJoinKeyColumn is set without listSourceTable + listColumn — ignored.`
      );
    }

    const labelText = ctrl.label ? String(ctrl.label).trim() : listDisplayResolved || actualColName;

    const leftC = colLetter(col);
    const rightC = colLetter(col + tileWidth - 1);

    /* Label band (merged, coloured). */
    const labelRange = sheet.getRange(`${leftC}${labelRow}:${rightC}${labelRow}`);
    labelRange.values = [[labelText, '']];
    try { labelRange.merge(true); } catch { /* older host */ }
    labelRange.format.fill.color = labelFill;
    labelRange.format.font.color = labelFont;
    labelRange.format.font.bold = true;
    labelRange.format.font.size = 10;
    labelRange.format.horizontalAlignment = 'Left';
    labelRange.format.verticalAlignment = 'Center';

    /* Dropdown cell — single cell so data validation applies cleanly. The tile's second column
       gets the same background for visual consistency only (no validation). */
    const dropdownAddr = `${leftC}${valueRow}`;
    const dropdownCell = sheet.getRange(dropdownAddr);
    dropdownCell.values = [[SENTINEL]];
    dropdownCell.format.fill.color = cellFill;
    dropdownCell.format.font.color = cellFont;
    dropdownCell.format.font.bold = false;
    dropdownCell.format.font.size = 11;
    dropdownCell.format.horizontalAlignment = 'Left';
    dropdownCell.format.verticalAlignment = 'Center';
    if (tileWidth > 1) {
      const padRange = sheet.getRange(`${colLetter(col + 1)}${valueRow}:${rightC}${valueRow}`);
      padRange.format.fill.color = cellFill;
    }

    /* Card outline so each filter reads as a tile. */
    const cardRange = sheet.getRange(`${leftC}${labelRow}:${rightC}${valueRow}`);
    applyCardBorder(cardRange, borderHex);

    /* Distinct value list in a hidden column. We read the table body through the API and write
       an explicit vertical list — data validation in Excel Online does not honor `=AZ1#` spill
       references, so the dropdown would only show the first cell ("(All)"). A fixed `=$AZ$1:$AZ$N`
       range works in all hosts. */
    const helperCol = colLetter(HELPER_COL_START + placed);
    let listRowCount = 1;
    const populateDesc =
      populateBound.srcTable !== bound.srcTable || populateColIdx !== colResolved
        ? `${populateBound.srcTable}[${String(populateBound.headers[populateColIdx] ?? '').trim()}]`
        : `${bound.srcTable}[${actualColName}]`;
    try {
      listRowCount = await populateFilterDropdownListFromTable(
        context,
        wb,
        sheet,
        populateBound,
        populateColIdx,
        helperCol,
        i,
        warnings
      );
      await context.sync();
    } catch (populateErr) {
      warnings.push(
        `filterControls[${i}] could not read distinct values for ${populateDesc} (${populateErr.message || populateErr}) — only "${SENTINEL}" is available.`
      );
    }

    const listRef = `=$${helperCol}$1:$${helperCol}$${listRowCount}`;
    try {
      const dv = dropdownCell.dataValidation;
      dv.clear();
      dv.rule = {
        list: {
          inCellDropDown: true,
          source: listRef,
        },
      };
      dv.errorAlert = {
        style: 'Stop',
        showAlert: false,
        title: 'Filter',
        message: `Pick a value or keep "${SENTINEL}" to include everything.`,
      };
    } catch (dvErr) {
      warnings.push(`filterControls[${i}] data validation could not be applied (${dvErr.message || dvErr}); dropdown will accept free text.`);
    }

    /* Hide the helper column so it doesn't clutter the dashboard. */
    try {
      sheet.getRange(`${helperCol}:${helperCol}`).columnHidden = true;
    } catch { /* older host: leave visible */ }

    /* Register for formula integration. We key by the TABLE name (workbook-unique); formulas will
       then AND-mask against `$A$4`-style absolute refs so the cell follows the layout, not the user. */
    const key = `tbl::${sourceTable.trim().toLowerCase()}`;
    if (!registry.has(key)) registry.set(key, []);
    const regEntry = {
      columnName: actualColName,
      cellAddr: `$${leftC}$${valueRow}`,
      sentinel: SENTINEL,
      maskKind: 'direct',
    };
    if (listJoinKeyColumn && listSourceTable && listColumn && listDisplayResolved && listKeyResolved) {
      regEntry.maskKind = 'xlookup';
      regEntry.listTable = listSourceTable;
      regEntry.listDisplayColumn = listDisplayResolved;
      regEntry.listJoinKeyColumn = listKeyResolved;
    }
    registry.get(key).push(regEntry);

    col += tileStep;
    placed += 1;
  }

  /* Row-height nudges so the filter strip has presence similar to the KPI strip below it. */
  try { sheet.getRange(`A${labelRow}`).getEntireRow().rowHeight = 20; } catch { /* ignore */ }
  try { sheet.getRange(`A${valueRow}`).getEntireRow().rowHeight = 26; } catch { /* ignore */ }

  /* +1 blank row as a visual gap between the filter strip and the KPIs below. */
  return { nextRow: placed > 0 ? startRow + 3 : startRow, registry };
}

/**
 * Card-styled KPI tile: merged 2-col label band (colored fill + white text) on top, merged
 * 2-col big value cell underneath, optional merged 2-col italic subtitle, outline border.
 * A 1-col gap between cards keeps tiles visually separated. All styling always-on so any plan
 * the AI emits looks dashboard-like.
 *
 * Tile width = 2 cols, gap = 1 col → 6 tiles fit in columns A..R (18 cols).
 *
 * @returns {{ nextRow: number, valueRow: number|null, colByKpiIndex: number[], tileWidth: number }}
 */
function writeKpiStrip(sheet, startRow, kpis, style) {
  if (!kpis || !kpis.length) {
    return { nextRow: startRow, valueRow: null, colByKpiIndex: [], tileWidth: 2 };
  }
  const list = kpis.slice(0, 6);
  /* Use the dashboard accent palette when provided; fall back to a neutral Corporate-Blue. */
  const labelFill = style?.titleBarFillHex || '#1F4E79';
  const labelFont = style?.titleBarFontHex || '#FFFFFF';
  const valueFill = style?.tableHeaderFillHex || '#F4F7FB';
  const valueFont = style?.tableHeaderFontHex || '#1F3864';
  const borderHex = '#BFCBDE';

  const tileWidth = 2;
  const tileGap = 1;
  const tileStep = tileWidth + tileGap;

  const labelRow = startRow;
  const valueRow = startRow + 1;
  const subtitleRow = startRow + 2;
  const anySubtitle = list.some((k) => k.subtitle);

  let col = 1;
  const colByKpiIndex = [];
  for (let i = 0; i < list.length; i++) {
    const k = list[i];
    const leftC = colLetter(col);
    const rightC = colLetter(col + tileWidth - 1);
    colByKpiIndex.push(col);

    /* Label band. */
    const labelRange = sheet.getRange(`${leftC}${labelRow}:${rightC}${labelRow}`);
    labelRange.values = [[String(k.label ?? ''), '']];
    try { labelRange.merge(true); } catch { /* older host */ }
    labelRange.format.fill.color = labelFill;
    labelRange.format.font.color = labelFont;
    labelRange.format.font.bold = true;
    labelRange.format.font.size = 10;
    labelRange.format.horizontalAlignment = 'Left';
    labelRange.format.verticalAlignment = 'Center';

    /* Value cell (formula filled later). */
    const valueRange = sheet.getRange(`${leftC}${valueRow}:${rightC}${valueRow}`);
    valueRange.values = [['', '']];
    try { valueRange.merge(true); } catch { /* older host */ }
    valueRange.format.fill.color = valueFill;
    valueRange.format.font.color = valueFont;
    valueRange.format.font.bold = true;
    valueRange.format.font.size = 20;
    valueRange.format.horizontalAlignment = 'Left';
    valueRange.format.verticalAlignment = 'Center';
    if (k.numberFormat) {
      try {
        /* numberFormat applied across the merged area survives re-merge on most hosts. */
        valueRange.numberFormat = [[String(k.numberFormat), String(k.numberFormat)]];
      } catch { /* ignore */ }
    }

    /* Optional subtitle (kept even when only some KPIs have one — band height is consistent). */
    const subRange = sheet.getRange(`${leftC}${subtitleRow}:${rightC}${subtitleRow}`);
    if (k.subtitle) {
      subRange.values = [[String(k.subtitle), '']];
      try { subRange.merge(true); } catch { /* older host */ }
      subRange.format.fill.color = valueFill;
      subRange.format.font.italic = true;
      subRange.format.font.size = 9;
      subRange.format.font.color = '#667085';
      subRange.format.horizontalAlignment = 'Left';
      subRange.format.verticalAlignment = 'Center';
    } else if (anySubtitle) {
      /* Consistent card height: empty cell gets the card fill so the row aligns visually. */
      subRange.format.fill.color = valueFill;
    }

    /* Outline border around the whole card. */
    const lastRow = anySubtitle ? subtitleRow : valueRow;
    const cardRange = sheet.getRange(`${leftC}${labelRow}:${rightC}${lastRow}`);
    applyCardBorder(cardRange, borderHex);

    col += tileStep;
  }

  /* Row height nudges for a taller, tile-like band. */
  try { sheet.getRange(`A${labelRow}`).getEntireRow().rowHeight = 22; } catch { /* ignore */ }
  try { sheet.getRange(`A${valueRow}`).getEntireRow().rowHeight = 36; } catch { /* ignore */ }
  if (anySubtitle) {
    try { sheet.getRange(`A${subtitleRow}`).getEntireRow().rowHeight = 18; } catch { /* ignore */ }
  }

  const rowsUsed = anySubtitle ? 3 : 2;
  return {
    nextRow: startRow + rowsUsed + 1,
    valueRow,
    colByKpiIndex,
    tileWidth,
  };
}

function writeInsightStrip(sheet, startRow, insights) {
  if (!insights || !insights.length) return startRow;
  const list = insights.slice(0, 6);
  let col = 1;
  for (const ins of list) {
    const c = colLetter(col);
    sheet.getRange(`${c}${startRow}`).values = [[String(ins.label ?? '')]];
    sheet.getRange(`${c}${startRow}`).format.font.size = 9;
    sheet.getRange(`${c}${startRow + 1}`).values = [[String(ins.value ?? '')]];
    sheet.getRange(`${c}${startRow + 1}`).format.font.bold = true;
    sheet.getRange(`${c}${startRow + 1}`).format.font.size = 11;
    col += 3;
  }
  return startRow + 3;
}

/**
 * Wipes a worksheet so a new dashboard plan can be applied without duplicate sheets.
 * Removes charts and ListObjects first, then clears the used range.
 * @param {Excel.RequestContext} context
 * @param {Excel.Worksheet} sheet
 */
/**
 * If `preferred` is taken, returns the first free name (…2, …3) within Excel’s 31-char limit.
 * @param {Excel.RequestContext} context
 * @param {Excel.Workbook} wb
 * @param {string} preferred - already sanitized
 * @param {string} rawName - unsanitized fallback base for numeric suffixes
 */
async function pickUnusedSheetName(context, wb, preferred, rawName) {
  let candidate = preferred;
  let o = wb.worksheets.getItemOrNullObject(candidate);
  o.load('isNullObject');
  await context.sync();
  if (o.isNullObject) return candidate;

  const base = String(rawName || 'AI_Dashboard').replace(/[\\/*?:\[\]]/g, '_').trim() || 'AI_Dashboard';
  let n = 2;
  while (n < 500) {
    const suffix = String(n);
    const slice = Math.max(1, 31 - suffix.length);
    candidate = sanitizeSheetName(`${base.slice(0, slice)}${suffix}`);
    o = wb.worksheets.getItemOrNullObject(candidate);
    o.load('isNullObject');
    await context.sync();
    if (o.isNullObject) return candidate;
    n += 1;
  }
  return sanitizeSheetName(`${base.slice(0, 20)}_${Date.now()}`.slice(0, 31));
}

async function clearWorksheetForDashboardReuse(context, sheet) {
  sheet.charts.load('items');
  sheet.tables.load('items');
  await context.sync();

  for (let i = sheet.charts.items.length - 1; i >= 0; i -= 1) {
    sheet.charts.items[i].delete();
  }
  for (let i = sheet.tables.items.length - 1; i >= 0; i -= 1) {
    sheet.tables.items[i].delete();
  }
  await context.sync();

  const used = sheet.getUsedRangeOrNullObject();
  used.load('isNullObject');
  await context.sync();
  if (!used.isNullObject) {
    used.clear(Excel.ClearApplyTo.all);
  }
  await context.sync();
}

/**
 * @param {object} spec - dashboard object from API (camelCase). Optional: forceNewSheet — if true and
 *   the planned name exists, create a new worksheet with the first unused name instead of overwriting.
 * @returns {Promise<{ sheetName: string, warnings: string[], reusedExisting: boolean }>}
 */
export async function applyDashboardSpec(spec) {
  const warnings = [];
  const hasTables = !!(spec && spec.tables && spec.tables.length);
  const hasCustomBlocks = !!(spec && Array.isArray(spec.customBlocks) && spec.customBlocks.length);
  const hasKpis = !!(spec && Array.isArray(spec.kpis) && spec.kpis.length);
  if (!spec || (!hasTables && !hasCustomBlocks && !hasKpis)) {
    throw new Error('Plan has no tables, KPIs, or customBlocks to render.');
  }

  const rawName = spec.sheetName || 'AI_Dashboard';
  let sheetName = sanitizeSheetName(rawName);
  const tableCount = hasTables ? spec.tables.length : 0;
  /** @type {(null | { headerRow: number, dataStart: number, dataEnd: number, colCount: number, hasData: boolean })[]} */
  const rangesBySpecIndex = new Array(tableCount).fill(null);

  return Excel.run(async (context) => {
    const wb = context.workbook;
    const forceNewSheet = spec.forceNewSheet === true;

    let existing = wb.worksheets.getItemOrNullObject(sheetName);
    existing.load('isNullObject');
    await context.sync();

    let reusedExisting = false;
    let sheet;

    if (forceNewSheet && !existing.isNullObject) {
      sheetName = await pickUnusedSheetName(context, wb, sheetName, rawName);
      existing = wb.worksheets.getItemOrNullObject(sheetName);
      existing.load('isNullObject');
      await context.sync();
    }

    if (existing.isNullObject) {
      sheet = wb.worksheets.add(sheetName);
      reusedExisting = false;
    } else {
      sheet = existing;
      reusedExisting = true;
      await clearWorksheetForDashboardReuse(context, sheet);
    }
    await context.sync();

    /**
     * Cache of resolved tables by normalized table name so per-KPI bindings and filter-control
     * renderers don't need to rescan worksheets. Keyed on the table name only (workbook-unique)
     * and on `sheet::table` for safety.
     * @type {Map<string, { srcSheet: string, srcTable: string, headers: string[] }>}
     */
    const resolvedTablesByName = new Map();
    const tableKey = (t) => `tbl::${String(t || '').trim().toLowerCase()}`;
    const sheetTableKey = (s, t) => `st::${String(s || '').trim().toLowerCase()}::${String(t || '').trim().toLowerCase()}`;

    /**
     * Resolve headers for any target table in the workbook. Uses the cache first, then falls
     * back to a per-sheet ListObject lookup, then to the workbook-wide tables collection
     * (table names are workbook-unique). Hoisted because filter controls render BEFORE any
     * spec.tables block, so we need the resolver up front.
     */
    const resolveSourceTable = async (srcSheet, srcTable) => {
      const s = String(srcSheet || '').trim();
      const t = String(srcTable || '').trim();
      if (!t) return null;
      const cached =
        resolvedTablesByName.get(sheetTableKey(s, t)) ||
        resolvedTablesByName.get(tableKey(t));
      if (cached) return cached;

      let tblObj = null;
      if (s) {
        const ws = wb.worksheets.getItemOrNullObject(s);
        ws.load('isNullObject');
        await context.sync();
        if (!ws.isNullObject) {
          tblObj = ws.tables.getItemOrNullObject(t);
          tblObj.load('isNullObject');
          await context.sync();
          if (tblObj.isNullObject) tblObj = null;
        }
      }
      if (!tblObj) {
        tblObj = wb.tables.getItemOrNullObject(t);
        tblObj.load(['isNullObject', 'name']);
        await context.sync();
        if (tblObj.isNullObject) return null;
        tblObj.worksheet.load('name');
        await context.sync();
      }
      const hdr = tblObj.getHeaderRowRange();
      hdr.load('values');
      await context.sync();
      const headers = hdr.values && hdr.values[0]
        ? hdr.values[0].map((c) => (c == null ? '' : String(c)).trim())
        : [];
      const sheetResolved = tblObj.worksheet?.name || s;
      const entry = { srcSheet: sheetResolved, srcTable: t, headers };
      resolvedTablesByName.set(tableKey(t), entry);
      resolvedTablesByName.set(sheetTableKey(sheetResolved, t), entry);
      return entry;
    };

    let cursorRow = 1;
    cursorRow = applyStyleBanner(sheet, spec.style, cursorRow);

    /* Interactive filter dropdowns (optional). When present, every formula against a matching
       sourceTable is AND-masked with `((cell="(All)")+(tbl[Col]=cell))` so the dashboard
       recomputes live when the user picks a dropdown value. */
    const filterStrip = await writeFilterControls(
      context,
      wb,
      sheet,
      cursorRow,
      spec.filterControls,
      spec.style,
      resolveSourceTable,
      warnings
    );
    cursorRow = filterStrip.nextRow;
    if (filterStrip.registry.size > 0) {
      /* Blank spacer row between filter strip and KPIs for visual breathing room. */
      cursorRow += 1;
    }
    /** Controls scoped to a specific source table, used by every formula builder below. */
    const controlsForTable = (tn) => filterStrip.registry.get(tableKey(tn)) || [];

    const kpiStrip = writeKpiStrip(sheet, cursorRow, spec.kpis, spec.style);
    cursorRow = kpiStrip.nextRow;
    cursorRow = writeInsightStrip(sheet, cursorRow, spec.insights);
    if (spec.kpis?.length || spec.insights?.length) {
      cursorRow += 1;
    }

    /** First resolved source block — used so every KPI value cell gets a formula to the same table. */
    let primarySource = null;
    let pushedLiveFormulaNote = false;

    for (let specIdx = 0; specIdx < tableCount; specIdx++) {
      const block = spec.tables[specIdx];
      const title = block.title ? String(block.title) : '';
      const srcSheet = String(block.sourceSheet || '');
      const srcTable = String(block.sourceTable || '');
      const maxRows = Math.min(Math.max(1, Number(block.maxRows) || 500), 2000);
      const spillCap = Math.min(8191, Math.max(200, Number(block.maxRows) || 6000));
      const filters = block.filters || [];

      if (title) {
        const titleCell = sheet.getRange(`A${cursorRow}`);
        titleCell.values = [[title]];
        titleCell.format.font.bold = true;
        titleCell.format.font.size = 12;
        if (spec.style?.tableHeaderFontHex) titleCell.format.font.color = spec.style.tableHeaderFontHex;
        cursorRow += 1;
      }

      const ws = wb.worksheets.getItemOrNullObject(srcSheet);
      ws.load('isNullObject');
      await context.sync();
      if (ws.isNullObject) {
        warnings.push(`Skipped block — worksheet not found: ${srcSheet}`);
        continue;
      }

      const tbl = ws.tables.getItemOrNullObject(srcTable);
      tbl.load('isNullObject');
      await context.sync();
      if (tbl.isNullObject) {
        warnings.push(`Skipped block — table not found: ${srcTable} on ${srcSheet}`);
        continue;
      }

      const headerRange = tbl.getHeaderRowRange();
      const bodyRange = getTableDataBodyRange(tbl);
      headerRange.load('values');
      bodyRange.load(['rowCount', 'columnCount']);
      await context.sync();

      const headers = (headerRange.values && headerRange.values[0])
        ? headerRange.values[0].map((c) => (c == null ? '' : String(c)).trim())
        : [];

      /* Populate lookup so per-KPI bindings find this table without a second worksheet scan. */
      if (headers.length) {
        const entry = { srcSheet, srcTable, headers };
        resolvedTablesByName.set(tableKey(srcTable), entry);
        resolvedTablesByName.set(sheetTableKey(srcSheet, srcTable), entry);
      }

      /* KPI totals must use a table with an amount column — prefer Raw_TrialBalance over master/mapping blocks. */
      const sumColProbe = pickDefaultSumColumnName(headers);
      if (headers.length && sumColProbe) {
        const blob = `${srcSheet}\t${srcTable}`.toLowerCase();
        const isTrialBalance = /trial|balance|tbl_rawtrial/i.test(blob);
        if (!primarySource || isTrialBalance) {
          primarySource = { srcSheet, srcTable, headers, filters };
        }
      }

      let liveFormula = null;
      if (block.aggregate?.keyColumns?.length && block.aggregate?.sumColumns?.length) {
        const aggForLive =
          block.aggregate.sumColumns.length > 1
            ? {
                ...block.aggregate,
                sumColumns: [block.aggregate.sumColumns[0]],
              }
            : block.aggregate;
        if (block.aggregate.sumColumns.length > 1) {
          warnings.push(
            `Block ${specIdx}: multiple sum columns — live formula uses first sum column only: ${block.aggregate.sumColumns[0]}`
          );
        }
        liveFormula = buildLiveAggregateFormula(srcSheet, srcTable, headers, aggForLive, filters, controlsForTable(srcTable));
      } else if ((block.columns || []).length) {
        liveFormula = buildLiveProjectionFormula(srcSheet, srcTable, headers, block.columns, filters, controlsForTable(srcTable));
      }

      let outHeaders;

      try {
        if (liveFormula) {
          if (block.aggregate?.keyColumns?.length && block.aggregate?.sumColumns?.length) {
            const keys = block.aggregate.keyColumns || [];
            const sums = block.aggregate.sumColumns || [];
            const sumColUse = sums[0];
            const ki = keys.map((k) => resolveColumnIndex(k, headers));
            const si = resolveColumnIndex(sumColUse, headers);
            const unknownKeys = keys.filter((_, ix) => ki[ix] < 0);
            if (unknownKeys.length || si < 0) {
              const missing = [
                ...unknownKeys.map((k) => `key "${k}"`),
                ...(si < 0 ? [`sum "${sumColUse}"`] : []),
              ];
              throw new Error(`unknown column(s): ${missing.join(', ')} — available: ${exampleHeaderList(headers)}`);
            }
            const keyLabels = ki.map((i) => String(headers[i] ?? '').trim());
            const sumLabels = [String(headers[si] ?? '').trim()];
            outHeaders = [...keyLabels, ...sumLabels];
          } else {
            const cols = block.columns || [];
            const colIdx = cols.map((c) => resolveColumnIndex(c, headers));
            const missing = cols.filter((_, ix) => colIdx[ix] < 0);
            if (missing.length) {
              throw new Error(`unknown column(s): ${missing.map((m) => `"${m}"`).join(', ')} — available: ${exampleHeaderList(headers)}`);
            }
            outHeaders = colIdx.map((i, idx) => {
              const h = String(headers[i] ?? '').trim();
              return h || String(cols[idx] ?? '').trim();
            });
          }
        } else {
          warnings.push(
            `Skipped block ${specIdx} — no buildable formula for "${srcTable}". Check that columns/keys/sumColumn exist. Available headers: ${exampleHeaderList(headers)}.`
          );
          continue;
        }
      } catch (e) {
        warnings.push(`Skipped block — "${srcTable}": ${e.message || e}`);
        continue;
      }

      const normHeaders = normalizeHeaderRow(outHeaders);
      if (normHeaders.length === 0) {
        warnings.push(`Skipped block — no output columns (table ${specIdx}).`);
        continue;
      }

      const headerRow = cursorRow;
      const dataStart = cursorRow + 1;
      let dataEnd;
      let hasData;
      let outputColCount = normHeaders.length;

      dataEnd = dataStart + spillCap - 1;
      hasData = true;

      let lastCol = colLetter(normHeaders.length);
      const hdrRg = sheet.getRange(`A${headerRow}:${lastCol}${headerRow}`);
      hdrRg.values = [normHeaders];
      hdrRg.format.font.bold = true;
      if (spec.style?.tableHeaderFillHex) hdrRg.format.fill.color = spec.style.tableHeaderFillHex;
      if (spec.style?.tableHeaderFontHex) hdrRg.format.font.color = spec.style.tableHeaderFontHex;

      let usedLiveFormulaOk = false;
      if (liveFormula) {
        try {
          sheet.getRange(`A${dataStart}`).formulas = [[liveFormula]];
          await context.sync();
          usedLiveFormulaOk = true;
          // Do not numberFormat the entire spill reservation — failures queue until the next sync() and
          // surface as RichApi "internal error" on Excel Online. Spill cells inherit sheet default format.
          if (!pushedLiveFormulaNote) {
            pushedLiveFormulaNote = true;
            warnings.push(
              'Live spill formulas reference your source tables — refresh is automatic when source data changes (Microsoft 365).'
            );
          }
          /* Aggregate blocks: the last OUTPUT column is the sum — overlay data bars for at-a-glance comparison. */
          if (block.aggregate?.keyColumns?.length && block.aggregate?.sumColumns?.length && outputColCount > 1) {
            applyDataBarsToColumn(sheet, lastCol, dataStart, dataEnd, spec.style);
          }
        } catch (e) {
          let recovered = false;
          if (block.aggregate?.keyColumns?.length && block.aggregate.sumColumns?.length) {
            const retryCols = [...block.aggregate.keyColumns, block.aggregate.sumColumns[0]];
            const retryF = buildLiveProjectionFormula(srcSheet, srcTable, headers, retryCols, filters, controlsForTable(srcTable));
            if (retryF) {
              try {
                sheet.getRange(`A${dataStart}`).formulas = [[retryF]];
                await context.sync();
                usedLiveFormulaOk = true;
                recovered = true;
                warnings.push(
                  `Block ${specIdx}: primary aggregate formula failed (${e.message || e}); applied projection-style formula (still references ${srcTable}[#Data]).`
                );
                if (!pushedLiveFormulaNote) {
                  pushedLiveFormulaNote = true;
                  warnings.push(
                    'Live spill formulas reference your source tables — refresh is automatic when source data changes (Microsoft 365).'
                  );
                }
              } catch (e2) {
                warnings.push(`Block ${specIdx}: fallback live formula also failed (${e2.message || e2}).`);
              }
            }
          }
          if (!recovered) {
            const sumColGuess =
              block.aggregate?.sumColumns?.[0] != null
                ? String(block.aggregate.sumColumns[0]).trim()
                : pickDefaultSumColumnName(headers);
            const fallbackF = sumColGuess
              ? buildTotalSumFormula(srcSheet, srcTable, headers, sumColGuess, filters)
              : null;
            const bare = fallbackF || (sumColGuess ? bareSumDataColumnFormula(srcSheet, srcTable, sumColGuess) : null);
            if (bare) {
              try {
                sheet.getRange(`A${headerRow}:${lastCol}${headerRow}`).clear(Excel.ClearApplyTo.contents);
                sheet.getRange(`A${headerRow}`).values = [[`Σ ${sumColGuess} (from ${srcTable})`]];
                sheet.getRange(`A${dataStart}`).formulas = [[bare]];
                try {
                  sheet.getRange(`A${dataStart}`).numberFormat = [['#,##0.00']];
                } catch { /* ignore */ }
                dataEnd = dataStart;
                outputColCount = 1;
                lastCol = 'A';
                usedLiveFormulaOk = true;
                recovered = true;
                hasData = true;
                await context.sync();
                warnings.push(
                  `Block ${specIdx}: complex spill formulas failed; placed SUMIFS/SUM on ${srcTable}[[#Data],[${sumColGuess}]]${filters.length ? ' with your filters' : ''} in A${dataStart} — still linked to your table. Re-ask for a grouped layout if needed.`
                );
              } catch (e3) {
                warnings.push(`Block ${specIdx}: plain SUM fallback failed (${e3.message || e3}).`);
                hasData = false;
              }
            } else {
              warnings.push(
                `Block ${specIdx}: Excel rejected live table formula (${e.message || e}). No static values were written — adjust filters or replan.`
              );
              hasData = false;
            }
          }
        }
      }

      rangesBySpecIndex[specIdx] = {
        headerRow,
        dataStart,
        dataEnd,
        colCount: outputColCount,
        hasData,
      };

      if (hasData && (!liveFormula || !usedLiveFormulaOk)) {
        try {
          const addr = `A${headerRow}:${lastCol}${dataEnd}`;
          const tOut = sheet.tables.add(addr, true);
          tOut.name = sanitizeTableName(sheetName, specIdx);
        } catch (e) {
          warnings.push(`Could not create Excel table on block ${specIdx}: ${e.message || e}`);
        }
      } else if (!hasData) {
        warnings.push(`Table block ${specIdx} has no data rows after filters — chart cannot use this block.`);
      }

      cursorRow = Math.max(dataEnd, headerRow) + 3;
      await context.sync();
    }

    if (kpiStrip.valueRow != null && spec.kpis?.length) {
      /** Build + write one KPI cell. Returns the formula written so retry path can reuse it. */
      const writeKpi = async (ki, kpi) => {
        if (kpiStrip.colByKpiIndex[ki] == null) return null;
        const col = colLetter(kpiStrip.colByKpiIndex[ki]);
        const cell = sheet.getRange(`${col}${kpiStrip.valueRow}`);

        /* 1) Explicit per-KPI binding — the model named a table + column. */
        let formula = null;
        let reason = null;
        if (kpi.sourceTable) {
          const bound = await resolveSourceTable(kpi.sourceSheet, kpi.sourceTable);
          if (!bound) {
            warnings.push(`KPI "${kpi.label || ki}" skipped — table not found: ${kpi.sourceTable}${kpi.sourceSheet ? ` on ${kpi.sourceSheet}` : ''}.`);
            return null;
          }
          const built = buildAggregateKpiFormula({
            tableName: bound.srcTable,
            headers: bound.headers,
            aggregation: kpi.aggregation,
            sumColumn: kpi.sumColumn,
            filters: kpi.filters || [],
            controls: controlsForTable(bound.srcTable),
          });
          formula = built.formula;
          reason = built.reason;
        }

        /* 2) Fall back to primary source + default amount column. */
        if (!formula && primarySource) {
          const sumName = pickDefaultSumColumnName(primarySource.headers);
          if (sumName) {
            formula = buildTotalSumFormula(
              primarySource.srcSheet,
              primarySource.srcTable,
              primarySource.headers,
              sumName,
              primarySource.filters,
              controlsForTable(primarySource.srcTable)
            );
            if (!formula) reason = reason || 'primary source fallback formula could not be built.';
          } else {
            reason = reason || 'no BalanceChange/amount column on primary source table.';
          }
        }

        if (!formula) {
          warnings.push(`KPI "${kpi.label || ki}" left empty — ${reason || 'no source table resolved'}.`);
          return null;
        }

        try {
          cell.formulas = [[formula]];
          cell.numberFormat = [[String(kpi.numberFormat || '#,##0.00')]];
        } catch (kpiErr) {
          warnings.push(`KPI "${kpi.label || ki}" formula rejected (${kpiErr.message || kpiErr}).`);
          return null;
        }
        return formula;
      };

      /** Build and write formulas sequentially; Office.js batches them into one sync below. */
      for (let ki = 0; ki < spec.kpis.length; ki++) {
        await writeKpi(ki, spec.kpis[ki] || {});
      }

      try {
        await context.sync();
      } catch (syncErr) {
        /* Retry unbound KPIs with bare SUM on [#Data] of primary source — most robust fallback. */
        warnings.push(`KPI row sync (${syncErr.message || syncErr}) — retrying with plain SUM on [#Data] column.`);
        const sumNameForKpi = primarySource ? pickDefaultSumColumnName(primarySource.headers) : null;
        const retryF = primarySource && sumNameForKpi
          ? (buildTotalSumFormula(
              primarySource.srcSheet,
              primarySource.srcTable,
              primarySource.headers,
              sumNameForKpi,
              primarySource.filters,
              controlsForTable(primarySource.srcTable)
            ) || bareSumDataColumnFormula(
              primarySource.srcSheet,
              primarySource.srcTable,
              sumNameForKpi
            ))
          : null;
        if (retryF) {
          for (let ki = 0; ki < spec.kpis.length; ki++) {
            if (kpiStrip.colByKpiIndex[ki] == null) continue;
            const col = colLetter(kpiStrip.colByKpiIndex[ki]);
            const c = sheet.getRange(`${col}${kpiStrip.valueRow}`);
            c.formulas = [[retryF]];
            try { c.numberFormat = [['#,##0.00']]; } catch { /* ignore */ }
          }
          try { await context.sync(); } catch (e2) {
            warnings.push(`KPI retry formula also failed: ${e2.message || e2}`);
          }
        }
      }
    }

    /**
     * Custom blocks — formula escape hatch for requests the structured schema can't express
     * (rolling windows, YoY %, pivot-style crosstabs, bespoke LET/LAMBDA layouts). Formulas are
     * strings that Excel parses itself — if Excel rejects one we surface the error + the formula
     * so the user can iterate with the model. Nothing is eval'd on the client.
     */
    if (Array.isArray(spec.customBlocks) && spec.customBlocks.length) {
      for (let bi = 0; bi < spec.customBlocks.length; bi++) {
        const blk = spec.customBlocks[bi] || {};
        const anchor = String(blk.anchor || '').trim().toUpperCase();
        const rawFormula = String(blk.formula || '').trim();
        if (!anchor || !/^[A-Z]+\d+$/.test(anchor)) {
          warnings.push(`customBlocks[${bi}] skipped — anchor "${blk.anchor}" is not a valid cell address (e.g. "T5").`);
          continue;
        }
        if (!rawFormula) {
          warnings.push(`customBlocks[${bi}] skipped — empty formula.`);
          continue;
        }
        const formula = rawFormula.startsWith('=') ? rawFormula : `=${rawFormula}`;
        try {
          if (blk.title) {
            /* Title goes one row above the anchor when there's room; otherwise skipped silently. */
            const match = /^([A-Z]+)(\d+)$/.exec(anchor);
            if (match) {
              const titleRow = Math.max(1, Number(match[2]) - 1);
              const titleCell = sheet.getRange(`${match[1]}${titleRow}`);
              titleCell.values = [[String(blk.title)]];
              titleCell.format.font.bold = true;
              titleCell.format.font.size = 11;
              if (spec.style?.tableHeaderFontHex) {
                titleCell.format.font.color = spec.style.tableHeaderFontHex;
              }
            }
          }
          const cell = sheet.getRange(anchor);
          cell.formulas = [[formula]];
          if (blk.numberFormat) {
            try { cell.numberFormat = [[String(blk.numberFormat)]]; } catch { /* ignore */ }
          }
          await context.sync();
        } catch (e) {
          warnings.push(
            `customBlocks[${bi}] at ${anchor} rejected by Excel (${e.message || e}). Formula was: ${formula}`
          );
        }
      }
    }

    const chartDefs = [
      ...(Array.isArray(spec.charts) ? spec.charts : []),
      ...(spec.chart ? [spec.chart] : []),
    ].filter(Boolean);

    /**
     * Chart layout: 2-column grid when we have ≥2 charts, stacked single column otherwise.
     * Left column spans A..H (8 cols, rows R..R+14); right column spans J..Q (same rows) —
     * leaves one blank column between for visual breathing room. Each row-band is 16 rows tall.
     * The grid is what makes the sheet read as a "dashboard" rather than a stack of tables.
     */
    const gridMode = chartDefs.length >= 2;
    const GRID_LEFT_START = 1;     // A
    const GRID_LEFT_END = 8;       // H
    const GRID_RIGHT_START = 10;   // J
    const GRID_RIGHT_END = 17;     // Q
    const BAND_HEIGHT = 16;
    const CHART_VIS_ROWS = 14;

    let chartBandStart = cursorRow;
    for (let ci = 0; ci < chartDefs.length; ci++) {
      const ch = chartDefs[ci];
      let ti = Number(ch.tableIndex);
      if (!Number.isFinite(ti)) ti = 0;
      ti = Math.min(Math.max(0, Math.floor(ti)), tableCount - 1);

      let tr = rangesBySpecIndex[ti];
      if (!tr || !tr.hasData || tr.dataEnd < tr.dataStart) {
        tr = rangesBySpecIndex.find((r) => r && r.hasData && r.dataEnd >= r.dataStart) || null;
      }
      if (!tr) {
        warnings.push(`Chart ${ci + 1} skipped — no data block with rows to chart.`);
        continue;
      }
      try {
        const left = colLetter(1);
        const right = colLetter(tr.colCount);
        const chartTypeEnum = mapChartType(ch.type || ch.chartType);
        let endRow = Math.min(tr.dataEnd, tr.dataStart + MAX_CHART_SOURCE_ROWS - 1);
        if (endRow < tr.dataStart) endRow = tr.dataStart;
        const dataAddr = `${left}${tr.headerRow}:${right}${endRow}`;
        const dataRange = sheet.getRange(dataAddr);
        const chart = sheet.charts.add(chartTypeEnum, dataRange, Excel.ChartSeriesBy.auto);
        chart.title.text = String(ch.title || `Chart ${ci + 1}`);

        let anchorStartCol;
        let anchorEndCol;
        let anchorTopRow;
        if (gridMode) {
          const inRight = ci % 2 === 1;
          anchorStartCol = inRight ? GRID_RIGHT_START : GRID_LEFT_START;
          anchorEndCol = inRight ? GRID_RIGHT_END : GRID_LEFT_END;
          anchorTopRow = chartBandStart + Math.floor(ci / 2) * BAND_HEIGHT;
        } else {
          anchorStartCol = 1;
          anchorEndCol = Math.min(8, tr.colCount + 2);
          anchorTopRow = chartBandStart + ci * BAND_HEIGHT;
        }
        chart.setPosition(
          sheet.getRange(`${colLetter(anchorStartCol)}${anchorTopRow}`),
          sheet.getRange(`${colLetter(anchorEndCol)}${anchorTopRow + CHART_VIS_ROWS}`)
        );
        await context.sync();
      } catch (e) {
        warnings.push(`Chart ${ci + 1} failed: ${e.message || e}`);
      }
    }

    /**
     * Autofit used columns so card labels / aggregate headers never get clipped to "AccountCl".
     * Done at the very end so it reflects every title/ value/ formula that has been written.
     * Wrapped in try/catch — older hosts or huge sheets occasionally reject autofit with "internal error".
     */
    try {
      const used = sheet.getUsedRangeOrNullObject();
      used.load('isNullObject');
      await context.sync();
      if (!used.isNullObject) {
        used.format.autofitColumns();
        await context.sync();
      }
    } catch (autofitErr) {
      warnings.push(`Column autofit skipped (${autofitErr.message || autofitErr}).`);
    }

    try {
      sheet.activate();
    } catch {
      /* activation can fail in protected / co-authoring scenarios */
    }
    await context.sync();

    return { sheetName, warnings, reusedExisting };
  });
}
